/**
 * Node Removal Tracking Utility
 *
 * Shared utility for tracking node removals to prevent scrollHeight-based
 * height updates from using stale values during/after large block node deletion.
 *
 * This is particularly important for nodes that significantly affect editor
 * height (e.g., SelfContractNode ~700px, DecisionNode ~500px).
 *
 * Usage:
 * 1. Before removing a node, call markNodeRemoval(editor)
 * 2. The blocking window lasts for NODE_REMOVAL_BLOCK_DURATION (1000ms)
 * 3. The editor's update listener should check isPendingNodeRemoval(editor)
 *    and skip scrollHeight checks during the blocking window
 * 4. The flag auto-clears after the timeout - no manual cleanup needed
 */

import type { LexicalEditor } from "lexical";

// Use a WeakMap to track pending node removals per editor instance
// This prevents scrollHeight from using stale values during/after node removal
const pendingNodeRemovalMap = new WeakMap<LexicalEditor, boolean>();

// Extended tracking: timestamp of last node removal for extended protection
const nodeRemovalTimestampMap = new WeakMap<LexicalEditor, number>();

// Duration (in ms) to block scrollHeight-based height updates after node removal
// Extended to 1000ms to account for cache reload delays
export const NODE_REMOVAL_BLOCK_DURATION = 1000;

/**
 * Mark that a node removal is in progress for the given editor.
 * This should be called BEFORE removing a node in the editor.
 *
 * @param editor - The Lexical editor instance
 */
export function markNodeRemoval(editor: LexicalEditor): void {
  pendingNodeRemovalMap.set(editor, true);
  nodeRemovalTimestampMap.set(editor, Date.now());
}

/**
 * Check if a node removal is currently in progress for the given editor.
 * This function auto-clears the flag after NODE_REMOVAL_BLOCK_DURATION has elapsed.
 *
 * @param editor - The Lexical editor instance
 * @returns true if node removal is in progress (within the blocking window)
 */
export function isPendingNodeRemoval(editor: LexicalEditor): boolean {
  // Check the timestamp-based blocking window with auto-cleanup
  const timestamp = nodeRemovalTimestampMap.get(editor);
  if (!timestamp) return false;

  const elapsed = Date.now() - timestamp;
  const isBlocked = elapsed < NODE_REMOVAL_BLOCK_DURATION;

  if (!isBlocked) {
    // Auto-clear after timeout to prevent memory leaks
    // WeakMap entries will be garbage collected when the editor is destroyed
    nodeRemovalTimestampMap.delete(editor);
    pendingNodeRemovalMap.delete(editor);
  }

  return isBlocked;
}
